import { useState } from "react";

import { zodResolver } from "@hookform/resolvers/zod";
import { LoggedInHTTPError } from "@impero-com/common/errorPages/loggedInHTTPError";
import { useTranslation } from "@impero-com/common/utils/i18n";
import {
    ActionSpec,
    ActionSpecStatus,
    Button,
    emitNotification,
    InformationModal,
} from "@impero-com/ui";
import { useForm } from "react-hook-form";
import { useNavigate, useParams } from "react-router-dom";

import { StickyFooter } from "components/stickyFooter/footer";
import { FormFooterWrapper } from "components/stickyFooter/FormFooterWrapper";
import { ViewCancelButton } from "components/stickyFooter/ViewCancelButton";
import deleteIcon from "img/delete.svg";
import { useTestingProgramQuery } from "org/controlTesting/testingProgram/view/queries/useTestingProgramQuery";
import {
    buildTestingProgramTestingControlsListUrl,
    testingControlIdParamSchema,
} from "org/controlTesting/urlUtils";
import { useTagsSuspenseQuery } from "org/tags/queries/useTagsQuery";
import { Guid } from "utils/guid";

import { OrgApplicationBar } from "../../../../components/applicationBar/orgApplicationBar";

import { useTestingControlViewAnchors } from "./consts";
import { useDeleteTestingControlMutation } from "./queries/useDeleteTestingControlMutation";
import { useTestingControlQuery } from "./queries/useTestingControlQuery";
import { useUpdateTestingControlMutation } from "./queries/useUpdateTestingControlMutation";
import { TestingControlForm } from "./TestingControlForm";
import {
    TestingControlEditSchema,
    TestingControlFormSchema,
    getTestingControlSchema,
} from "./testingControlSchema";
import {
    toTestingControlEditSchemaData,
    toUpdatableTestingControl,
} from "./utils";

export function TestingControlEditPage() {
    const params = useParams();
    const unparsedTestingControlId = params.id;
    const testingControlIdParseResult = testingControlIdParamSchema.safeParse(
        unparsedTestingControlId
    );

    if (testingControlIdParseResult.success === false) {
        console.error(
            "Testing control id must be a UUID, got:",
            unparsedTestingControlId
        );
        return <LoggedInHTTPError code={400} />;
    }
    const testingControlId: Guid = testingControlIdParseResult.data;
    return <View testingControlId={testingControlId} />;
}

interface ViewProps {
    testingControlId: Guid;
}
function View({ testingControlId }: ViewProps) {
    const { t } = useTranslation();
    const navigate = useNavigate();

    const { data } = useTestingControlQuery(testingControlId);
    const { resource } = data.content;
    const { data: testingProgramData } = useTestingProgramQuery(
        resource.testingProgramId
    );
    const { testingProgramId } = data.content.resource;

    const { deleteAction, deleteDialogComponent } = useDeleteAction(
        data.content.actions.delete,
        testingControlId,
        testingProgramId
    );

    const schema = getTestingControlSchema("EDIT", t);
    const { data: tagsData } = useTagsSuspenseQuery();
    const { formState, handleSubmit, control, trigger, setValue, subscribe } =
        useForm<TestingControlFormSchema>({
            resolver: zodResolver(schema),
            mode: "onBlur",
            values: toTestingControlEditSchemaData(resource, tagsData),
        });
    const { mutateAsync: updateTestingControl, isPending: isUpdating } =
        useUpdateTestingControlMutation();

    const handleSubmitClick = handleSubmit(
        async (data) => {
            const updatableTestingControl = toUpdatableTestingControl(
                data as TestingControlEditSchema,
                resource
            );
            await updateTestingControl({
                testingControlId,
                data: updatableTestingControl,
            });
            navigate(
                buildTestingProgramTestingControlsListUrl(testingProgramId)
            );
        },
        (error) => {
            console.error(error);
            emitNotification(
                "error",
                t("TestingControlView_SubmitValidationError")
            );
        }
    );

    const disabled = !data.content.actions.edit;
    const anchors = useTestingControlViewAnchors("EDIT");
    return (
        <OrgApplicationBar
            title={{
                title: data.content.resource.title,
                breadCrumbs: [
                    {
                        label: testingProgramData.content.resource.name,
                        url: buildTestingProgramTestingControlsListUrl(
                            testingProgramId
                        ),
                    },
                ],
            }}
            navigation={{
                type: "Anchors",
                anchors,
            }}
            actions={{
                actions: [deleteAction],
                id: "editTestingControlView",
            }}
        >
            {deleteDialogComponent}
            <FormFooterWrapper>
                <TestingControlForm
                    disabled={disabled}
                    control={control}
                    trigger={trigger}
                    setValue={setValue}
                    subscribe={subscribe}
                    pageType="EDIT"
                />
                {!disabled && (
                    <StickyFooter>
                        <Button
                            size="large"
                            variant="primary"
                            disabled={!formState.isDirty}
                            loading={isUpdating}
                            onClick={() => void handleSubmitClick()}
                        >
                            {t("TestingControlView_UpdateButton")}
                        </Button>
                        <ViewCancelButton
                            isDirty={formState.isDirty}
                            bodyLabel={t(
                                "TestingControlView_ConfirmCancel_Body"
                            )}
                        />
                    </StickyFooter>
                )}
            </FormFooterWrapper>
        </OrgApplicationBar>
    );
}

function useDeleteAction(
    hasDeleteAction: boolean,
    testingControlId: Guid,
    testingProgramId: number
) {
    const { t } = useTranslation();
    const { mutateAsync: mutateDelete, isPending: isDeleting } =
        useDeleteTestingControlMutation();
    const navigate = useNavigate();

    const [dialogOpen, setDialogOpen] = useState(false);

    const handleDeleteClick = async () => {
        await mutateDelete({ data: [testingControlId] });
        navigate(buildTestingProgramTestingControlsListUrl(testingProgramId));
        emitNotification("success", t("TestingControlView_DeleteNotification"));
    };
    const actionButtonStatus: ActionSpecStatus = hasDeleteAction
        ? { type: "ENABLED" }
        : {
              type: "DISABLED",
              reason: t("TestingControlView_DeleteAction_NoAccess"),
          };

    const deleteAction: ActionSpec = {
        id: "deleteTestingControl",
        icon: { svg: deleteIcon },
        label: t("TestingControlView_DeleteAction"),
        status: actionButtonStatus,
        onClick: () => setDialogOpen(true),
    };
    const deleteDialogComponent = (
        <InformationModal
            open={dialogOpen}
            onClose={() => setDialogOpen(false)}
            type="error"
            icon="delete"
            title={t("TestingControlView_DeleteDialog_Title")}
            footer={
                <>
                    <Button onClick={() => setDialogOpen(false)}>
                        {t("Cancel")}
                    </Button>
                    <Button
                        variant="destructive"
                        loading={isDeleting}
                        onClick={() => void handleDeleteClick()}
                    >
                        {t("Delete")}
                    </Button>
                </>
            }
        >
            {t("TestingControlView_DeleteDialog_Body")}
        </InformationModal>
    );
    return {
        deleteAction,
        deleteDialogComponent,
    };
}
